// @flow

import {
  connectRouter,
  routerMiddleware,
}                                                            from 'connected-react-router/immutable';
import * as Immutable                                        from 'immutable';
import PropTypes                                             from 'prop-types';
import type {
  Store,
  StoreEnhancer,
}                                                            from 'redux';
import {
  applyMiddleware,
  compose,
  createStore,
}                                                            from 'redux';
import persistState                                          from 'redux-localstorage';
import createSagaMiddleware                                  from 'redux-saga';
import { middleware as reduxSagaThunkMiddleware }            from 'redux-saga-thunk';
import thunk                                                 from 'redux-thunk';
import resetAllData                                          from './actions/resetAllData';
import createActionSyncMiddleware, { createStorageListener } from './modules/actionSync/store/middleware/createActionSyncMiddleware';
import personalContentEnhancer
                                                             from './modules/personalContent/store/enhancer/personalContentEnhancer';
import { makeLocation }                                      from './modules/react-router/domain/react-router.domain';
import rootReducer, { STATE_KEY_PERSONAL_CONTENT }           from './reducers/rootReducer';
import history                                               from './router/history';
import sagas                                                 from './sagas';
import googleAnalyticsMiddleware                             from './store/middleware/googleAnalyticsMiddleware';
import isStorageAllowed                                      from './utils/cookieSettings/isStorageAllowed';


export type State = Immutable.Map<string, *>;

let store: ?Store<State, *>;


// -----------------------------------------------------------------------------------------------------------------
// Config
// -----------------------------------------------------------------------------------------------------------------

const actionsToSync    = [];
const actionsNotToSync = [
  'ROUTER_HISTORY:REPLACE_PATH',
];


/**
 * Getter for store
 *
 * @return {Store}
 */
export function getStore() {

  if (!store) {

    /* eslint-disable no-underscore-dangle */

    const composeEnhancers =
            process.env.NODE_ENV !== "production" &&
            typeof window === 'object' &&
            window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ?
              window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
                // Specify extension’s options like name, actionsBlacklist, actionsCreators, serialize...
                serialize: {
                  options: true,
                },
              }) : compose;

    const sagaMiddleware = createSagaMiddleware();

    const enhancer: StoreEnhancer<*, *> = composeEnhancers(
      personalContentEnhancer({
        moduleStatePath: STATE_KEY_PERSONAL_CONTENT,
        publicBranches:  [
          ['apiData'],
          ['apiResources'],
          ['cookieSettings', 'components'],
          ['router'],
          ['routerHistory'], // Allow router history, as it only contains what's in the browser history already
          ['form', 'userCookieSettings'],
          ['form', '*', 'active'],
          ['form', '*', 'anyTouched'],
          ['form', '*', 'fields'],
          ['form', '*', 'registeredFields'],
          ['form', '*', 'syncErrors'],
        ],
        ignoreActions:   [String(resetAllData)],
      }),
      applyMiddleware(
        createActionSyncMiddleware({
          syncDecisionHandler: (action) => {
            if (
              /^ACCESSIBILITY:/.test(action.type)
              || /^AUTH:/.test(action.type)
              || /^COOKIE_SETTINGS:USER_SETTINGS:/.test(action.type)
              || /^LOCAL_SHORTLIST:/.test(action.type)
              || /^PERSONAL_CONTENT:/.test(action.type)
            ) {
              return true;
            }
            return false;
          },
        }),
        googleAnalyticsMiddleware,
        routerMiddleware(history),
        thunk,
        reduxSagaThunkMiddleware,
        sagaMiddleware,
      ),
      persistState(
        ['accessibility', 'auth', 'cookieSettings', 'localShortlist', 'personalContent', 'router', 'routerHistory'],
        {
          slicer:      (paths) =>
                         (state) => {
                           state = state.filter((value, key) => Immutable.Set(paths).contains(key));

                           if (!isStorageAllowed('userPreferences', state)) {
                             state = state.filter(
                               (value, key) => !Immutable.Set(['accessibility', 'auth', 'localShortlist'])
                                 .contains(key),
                             );
                           }

                           return state;
                         },
          deserialize: (serializedData) => {

            let state: Immutable.Map<string, *> =
                  // $FlowFixMe Immutable.fromJS() does not work well with flow
                  Immutable.fromJS(JSON.parse(serializedData));

            // Convert ordered sets
            state = state.setIn(
              ['localShortlist', 'serviceIds'],
              state.getIn(['localShortlist', 'serviceIds'], Immutable.List()).toOrderedSet(),
            );

            // Convert LocationRecords
            // @todo $FlowFixMe
            state = state.setIn(
              ['routerHistory', 'pathHistory'],
              state.getIn(['routerHistory', 'pathHistory'], Immutable.List())
                .map((historyItem: Immutable.Map<string, string>) => makeLocation(historyItem.toJS())),
            );

            return state;

          },
          merge:       (initialState, persistedState) => initialState.mergeDeep(persistedState),
        }),
    );

    const emptyState = Immutable.Map();

    store = createStore(
      connectRouter(history)(rootReducer),
      emptyState,
      enhancer,
    );

    createStorageListener(store);

    sagaMiddleware.run(sagas);

  }

  /* eslint-enable */

  return store;

}


export const resetStore = () => {
  store = null;
};


export const storePropType = PropTypes.shape({
  getState:  PropTypes.func.isRequired,
  dispatch:  PropTypes.func.isRequired,
  subscribe: PropTypes.func.isRequired,
});



// WEBPACK FOOTER //
// ./src/store.js